import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, userProfileAPI } from '../../services/apiClient';
import toast from 'react-hot-toast';
import Sidebar from './Sidebar';
import './UpdatePassword.css';
import '../admin/AdminForms.css';
import '../../styles/fonts.css';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isBusinessAdmin = userRole === 'business_admin';
  const showSidebar = isSuperAdmin || isBusinessAdmin;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      toast.error('All fields are required');
      return;
    }

    if (formData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        current_password: formData.currentPassword,
        new_password: formData.newPassword
      };

      // Admin + business admin share the admin password change endpoint
      const response = (isSuperAdmin || isBusinessAdmin)
        ? await authAPI.changeAdminPassword(payload)
        : await userProfileAPI.changePassword({
            ...payload,
            confirm_password: formData.confirmPassword
          });

      const successMsg = response?.data?.message || 'Password updated successfully!';
      toast.success(successMsg);
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        if (isBusinessAdmin) {
          navigate('/business-admin/dashboard');
        } else if (isSuperAdmin) {
          navigate('/admin');
        } else {
          navigate('/');
        }
      }, 1500);
    } catch (error) {
      console.error('Password update error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formContent = (
    <div className="form-container-card">
      <div className="form-header">
        <h1 className="chat-title">Update Password</h1>
        <p className="chat-subtitle">Use a strong, unique password to keep your account secure.</p>
      </div>

      <form onSubmit={handleSubmit} className="admin-form">
        <div className="newform-group">
          <label htmlFor="currentPassword">Current Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPasswords.current ? 'text' : 'password'}
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Enter current password"
              disabled={loading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => togglePasswordVisibility('current')}
              aria-label={showPasswords.current ? 'Hide current password' : 'Show current password'}
            >
              <i className={`ri-eye${showPasswords.current ? '-off' : ''}-line`}></i>
            </button>
          </div>
        </div>

        <div className="newform-group">
          <label htmlFor="newPassword">New Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPasswords.new ? 'text' : 'password'}
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Enter new password (min 8 characters)"
              disabled={loading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => togglePasswordVisibility('new')}
              aria-label={showPasswords.new ? 'Hide new password' : 'Show new password'}
            >
              <i className={`ri-eye${showPasswords.new ? '-off' : ''}-line`}></i>
            </button>
          </div>
        </div>

        <div className="newform-group">
          <label htmlFor="confirmPassword">Confirm New Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPasswords.confirm ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              disabled={loading}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => togglePasswordVisibility('confirm')}
              aria-label={showPasswords.confirm ? 'Hide confirmation password' : 'Show confirmation password'}
            >
              <i className={`ri-eye${showPasswords.confirm ? '-off' : ''}-line`}></i>
            </button>
          </div>
        </div>

        <div className="newform-actions">
          <button
            type="button"
            className="newform-button secondary"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="newform-button primary" disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </form>
    </div>
  );

  return showSidebar ? (
    <div className="page-container update-password-with-sidebar">
      <Sidebar />
      <div className="admin-form-page update-password-page">
        {formContent}
      </div>
    </div>
  ) : (
    <div className="admin-form-page update-password-page">
      {formContent}
    </div>
  );
};

export default UpdatePassword;
